import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Card, CardContent, Button, Typography, Stack,
} from '@mui/material';
import { AppBar } from '../components/AppBar';
import { useAuth } from '../auth/useAuth';

export function SignIn({ mode = 'signin' }: { mode?: 'signin' | 'signup' }) {
  const { signIn, ready, tokens } = useAuth();
  const isSignup = mode === 'signup';
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // If already authenticated, redirect away from the sign-in page
  useEffect(() => {
    if (ready && tokens) {
      const from = params.get('from') ?? '/dashboard';
      navigate(from, { replace: true });
    }
  }, [ready, tokens, navigate, params]);

  const onSignIn = async () => {
    // Store the return path so the /callback handler knows where to redirect after Auth0 returns
    const from = params.get('from') ?? '/dashboard';
    sessionStorage.setItem('auth-return-to', from);
    await signIn(isSignup ? { screenHint: 'signup' } : undefined);
    // Auth0 redirects away — the rest happens on return via /callback
  };

  // If we're on /callback (Auth0 redirect back), the AuthProvider handles
  // the code exchange. Once `ready` becomes true, navigate to the return path.
  if (ready && window.location.pathname === '/callback') {
    const from = sessionStorage.getItem('auth-return-to') ?? '/dashboard';
    sessionStorage.removeItem('auth-return-to');
    navigate(from, { replace: true });
  }

  return (
    <Box>
      <AppBar />
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography variant="h2" sx={{ mb: 4 }}>{isSignup ? 'Create your account' : 'Sign in'}</Typography>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={2.5}>
              <Typography variant="body1" sx={{ color: 'var(--ink-muted)' }}>
                {isSignup
                  ? "Sign up via Auth0 — you'll be redirected to the secure registration page, then returned here."
                  : "Sign in via Auth0 — you'll be redirected to the secure login page, then returned here."}
              </Typography>
              <Button onClick={onSignIn} variant="contained" size="large" fullWidth disabled={!ready}>
                {ready ? 'Continue to Auth0' : 'Loading…'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}